'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useGetMarketChainMetadata, useMarketConfigCatalog, useQueryClient as useQueryClientHook, useTurnkeySigningDeps, useTurnkeySubmissionDeps } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Panel, PanelContent } from '@/components/ui/panel';
import { D6_SCALE } from '@/config/solana';
import { useAllowRapidTrades } from '@/hooks/use-allow-rapid-trades';
import { useFormattedCurrency, useQuickAmounts } from '@/lib/currency';
import { useUserNetwork } from '@/lib/network/client';
import { useSubmitTransaction as useSubmitTransactionHook } from '@/lib/tx-engine/use-submit-transaction';
import { useInvalidateWalletBalances, useWalletBalances } from '@/queries/wallet-balance';
import { isTradeSubmissionBlocked, submitGuardedTrade, useTradeSubmissionLockStore } from '@/stores/trade-submission-lock';
import { ProposalFormDirectionTabs } from './proposal-form-direction-tabs';
import { FundSubmitButton, FundTab, StakeSummaryRow, WithdrawTab } from './proposal-form-tabs';
import { useProposalFund } from './proposal-fund';
import { useProposalWithdraw } from './proposal-withdraw';
import { limitBlockedResult, useDailyBackingLimit } from './use-daily-backing-limit';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OutcomeItem {
  index: number;
  label: string;
  imageUrl?: string | undefined;
  imageNsfw?: boolean | undefined;
  stakeAmount: string;
  percentage: number;
  backerCount: number;
}
interface UserStakePosition {
  outcomeIndex: number;
  amount: string;
  tokenPrice: string | null;
  tokensReceived: string | null;
}
interface ActiveSubmissionController {
  readonly controller: AbortController;
  readonly scope: string;
}
interface ScopedSubmissionController {
  readonly abortControllerRef: React.MutableRefObject<AbortController | null>;
  readonly isSubmitting: boolean;
  readonly setActiveAbortController: (controller: AbortController | null) => void;
}
interface ProposalFormProps {
  rewardsPanel?: React.ReactNode;
  proposalPubkey: string;
  proposalTitle: string;
  outcomes: OutcomeItem[];
  userStake: {
    positions: UserStakePosition[];
    totalAmount: string;
  } | null;
  /**
   * Legacy prop — kept for callsite back-compat after the spec-072 form
   * rewrite. The contract's `unback` instruction now reads the exit-fee
   * BPS from `MarketConfig.fee_bps_unbacking` (surfaced via `feeBpsUnbacking`
   * below). This number is no longer rendered anywhere.
   */
  withdrawFeePercent: number | undefined;
  /** `MarketConfig.fee_bps_backing` — basis points. Drives the Fund-tab estimate. */
  feeBpsBacking: number | undefined;
  /** `MarketConfig.fee_bps_unbacking` — basis points. Drives the Withdraw-tab estimate. */
  feeBpsUnbacking: number | undefined;
  /** When true, the withdraw tab is the default and fund tab is hidden */
  withdrawOnly?: boolean;
  /** When true, renders without the outer Panel wrapper (for use inside sheets). */
  bare?: boolean;
  /** Controlled value of the amount input (lifted to ProposalDetailRight). */
  amountInput: string;
  /** Setter for the controlled amount input. */
  onAmountInputChange: (next: string) => void;
  /** The proposal went live and the page is handing off to its market. */
  launching?: boolean;
}
function useScopedSubmissionController(scope) {
  const $ = _c(9);
  const [active, setActive] = useState(null);
  const abortControllerRef = useRef(null);
  let t0;
  if ($[0] !== scope) {
    t0 = controller => {
      setActive(current => {
        if (controller !== null) {
          return {
            controller,
            scope
          };
        }
        return current?.scope === scope ? null : current;
      });
    };
    $[0] = scope;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const setActiveAbortController = t0;
  let t1;
  let t2;
  if ($[2] !== active || $[3] !== scope) {
    t1 = () => {
      if (active !== null && active.scope !== scope) {
        active.controller.abort();
      }
      return () => active?.controller.abort();
    };
    t2 = [active, scope];
    $[2] = active;
    $[3] = scope;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  const t3 = active?.scope === scope;
  let t4;
  if ($[6] !== setActiveAbortController || $[7] !== t3) {
    t4 = {
      abortControllerRef,
      isSubmitting: t3,
      setActiveAbortController
    };
    $[6] = setActiveAbortController;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MIN_NON_PROPOSER_FUND = 10;
export function ProposalForm(props) {
  const $ = _c(3);
  if (props.feeBpsBacking === undefined || props.feeBpsUnbacking === undefined) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <UnavailableProposalConfig />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] !== props) {
    t0 = <ReadyProposalForm {...props} feeBpsBacking={props.feeBpsBacking} feeBpsUnbacking={props.feeBpsUnbacking} />;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
function UnavailableProposalConfig() {
  const $ = _c(14);
  const catalog = useMarketConfigCatalog();
  const tRoot = useTranslations();
  let t0;
  if ($[0] !== catalog.status || $[1] !== tRoot) {
    t0 = catalog.status === "loading" ? tRoot("launchpad.detail.backerRewards.loading") : tRoot("errors.server.unavailable");
    $[0] = catalog.status;
    $[1] = tRoot;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = catalog.isFetching;
  const t2 = catalog.isFetching;
  let t3;
  if ($[3] !== catalog) {
    t3 = () => catalog.retry();
    $[3] = catalog;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== tRoot) {
    t4 = tRoot("errorBoundary.retry");
    $[5] = tRoot;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== catalog.isFetching || $[8] !== t3 || $[9] !== t4) {
    t5 = <AnimatedButton disabled={t1} loading={t2} onClick={t3}>{t4}</AnimatedButton>;
    $[7] = catalog.isFetching;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t0 || $[12] !== t5) {
    t6 = <Panel><PanelContent role="status" className="flex flex-col gap-3">{t0}{t5}</PanelContent></Panel>;
    $[11] = t0;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function ReadyProposalForm(t0) {
  const $ = _c(73);
  const {
    rewardsPanel,
    proposalPubkey,
    outcomes,
    userStake,
    feeBpsBacking,
    feeBpsUnbacking,
    withdrawOnly: t1,
    bare: t2,
    amountInput,
    onAmountInputChange,
    launching: t3
  } = t0;
  const withdrawOnly = t1 === undefined ? false : t1;
  const bare = t2 === undefined ? false : t2;
  const launching = t3 === undefined ? false : t3;
  const t = useTranslations("launchpad.detail");
  const tBack = useTranslations("launchpad.back");
  const tWithdraw = useTranslations("launchpad.withdraw");
  const tForm = useTranslations("launchpad.form");
  const runFund = useProposalFund();
  const dailyBackingLimit = useDailyBackingLimit();
  const runWithdraw = useProposalWithdraw();
  const {
    formatBalance,
    toUsdc,
    currencySymbol,
    exchangeRate
  } = useFormattedCurrency();
  const {
    activeWallet
  } = useActiveWallet();
  const activeWalletAddress = activeWallet?.address;
  let t4;
  if ($[0] !== activeWalletAddress) {
    t4 = activeWalletAddress !== undefined ? [activeWalletAddress] : [];
    $[0] = activeWalletAddress;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    perWallet: balancePerWallet
  } = useWalletBalances(t4);
  const queryClient = useQueryClientHook();
  const invalidateWalletBalances = useInvalidateWalletBalances();
  const turnkeyDeps = useTurnkeySubmissionDeps();
  const submitTx = useSubmitTransactionHook();
  const {
    network
  } = useUserNetwork();
  const showTabs = !withdrawOnly;
  const [direction, setDirection] = useState(withdrawOnly ? "withdraw" : "fund");
  const launchingRef = useRef(launching);
  let t5;
  if ($[2] !== launching) {
    t5 = () => {
      launchingRef.current = launching;
    };
    $[2] = launching;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  useEffect(t5);
  const {
    abortControllerRef,
    isSubmitting,
    setActiveAbortController
  } = useScopedSubmissionController(`${network}:${activeWalletAddress ?? "__none__"}`);
  const [fundingControllers] = useState(_temp);
  const fundingScope = `${network}:${activeWalletAddress ?? "__none__"}:${proposalPubkey}`;
  let t6;
  let t7;
  if ($[4] !== fundingControllers || $[5] !== fundingScope) {
    t6 = () => () => {
      for (const [controller, scope] of fundingControllers) {
        if (scope === fundingScope) {
          controller.abort();
          fundingControllers.delete(controller);
        }
      }
    };
    t7 = [fundingControllers, fundingScope];
    $[4] = fundingControllers;
    $[5] = fundingScope;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[8] !== fundingControllers || $[9] !== launching) {
    t8 = () => {
      if (launching) {
        fundingControllers.clear();
      }
    };
    t9 = [launching, fundingControllers];
    $[8] = fundingControllers;
    $[9] = launching;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useEffect(t8, t9);
  const outstandingCount = useTradeSubmissionLockStore(_temp2);
  const hasUnknownTrade = useTradeSubmissionLockStore(_temp4);
  const swapPending = useTradeSubmissionLockStore(_temp5);
  const allowRapidTrades = useAllowRapidTrades();
  const fundingBlocked = isSubmitting || swapPending || hasUnknownTrade || !allowRapidTrades && outstandingCount > 0;
  const anySubmissionPending = isSubmitting || swapPending || outstandingCount > 0;
  const tTrade = useTranslations("trade");
  const formRootRef = useRef(null);
  const signingDeps = useTurnkeySigningDeps();
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = ["marketConfig", "quoteMint", "eventSchedule"];
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== proposalPubkey) {
    t11 = {
      pubkey: proposalPubkey,
      fields: t10
    };
    $[13] = proposalPubkey;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const t12 = activeWallet !== null;
  let t13;
  if ($[15] !== t12) {
    t13 = {
      enabled: t12
    };
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const chainMetadata = useGetMarketChainMetadata(t11, t13).data;
  const backingAccounts = chainMetadata === undefined ? undefined : {
    marketConfig: chainMetadata.marketConfig,
    quoteMint: chainMetadata.quoteMint,
    eventSchedule: chainMetadata.eventSchedule
  };
  const fundStakeOutcomeIndex = outcomes[0]?.index ?? 0;
  const balance = activeWalletAddress !== undefined ? balancePerWallet.get(activeWalletAddress) ?? 0 : 0;
  const parsedLocalAmount = Number.parseFloat(amountInput);
  const usdcAmount = Number.isNaN(parsedLocalAmount) ? 0 : toUsdc(parsedLocalAmount);
  const isValidFundAmount = !Number.isNaN(parsedLocalAmount) && usdcAmount >= MIN_NON_PROPOSER_FUND;
  const t14 = balance * exchangeRate;
  const t15 = MIN_NON_PROPOSER_FUND * exchangeRate;
  let t16;
  if ($[17] !== t14 || $[18] !== t15) {
    t16 = {
      maxLocal: t14,
      min: t15
    };
    $[17] = t14;
    $[18] = t15;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const quickAmounts = useQuickAmounts(t16);
  const totalStakedUsdc = userStake !== null && userStake.totalAmount.length > 0 ? Number(userStake.totalAmount) / D6_SCALE : 0;
  const backingFeeUsdc = isValidFundAmount ? usdcAmount * feeBpsBacking / 10000 : 0;
  const totalCostUsdc = isValidFundAmount ? usdcAmount + backingFeeUsdc : 0;
  const exceedsBalance = isValidFundAmount && totalCostUsdc > balance;
  const canWithdraw = totalStakedUsdc > 0;
  const unbackingFeeUsdc = totalStakedUsdc * feeBpsUnbacking / 10000;
  const netReturnUsdc = totalStakedUsdc - unbackingFeeUsdc;
  const fundSubmitDisabled = !activeWallet || outcomes.length === 0 || !isValidFundAmount || exceedsBalance || fundingBlocked || launching;
  const withdrawSubmitDisabled = !activeWallet || !canWithdraw || anySubmissionPending || launching;
  const directionSwitchDisabled = anySubmissionPending || launching;
  let t17;
  bb0: {
    if (launching) {
      let t18;
      if ($[20] !== tForm) {
        t18 = tForm("creatingMarket");
        $[20] = tForm;
        $[21] = t18;
      } else {
        t18 = $[21];
      }
      t17 = t18;
      break bb0;
    }
    if (fundingBlocked) {
      let t18;
      if ($[22] !== tBack) {
        t18 = tBack("submitting");
        $[22] = tBack;
        $[23] = t18;
      } else {
        t18 = $[23];
      }
      t17 = t18;
      break bb0;
    }
    if (!activeWallet) {
      let t18;
      if ($[24] !== tBack) {
        t18 = tBack("walletNotConnected");
        $[24] = tBack;
        $[25] = t18;
      } else {
        t18 = $[25];
      }
      t17 = t18;
      break bb0;
    }
    if (amountInput === "" || !isValidFundAmount) {
      let t18;
      if ($[26] !== t) {
        t18 = t("backCta");
        $[26] = t;
        $[27] = t18;
      } else {
        t18 = $[27];
      }
      t17 = t18;
      break bb0;
    }
    if (exceedsBalance) {
      let t18;
      if ($[28] !== tBack) {
        t18 = tBack("insufficientBalance");
        $[28] = tBack;
        $[29] = t18;
      } else {
        t18 = $[29];
      }
      t17 = t18;
      break bb0;
    }
    let t18;
    if ($[30] !== t) {
      t18 = t("backCta");
      $[30] = t;
      $[31] = t18;
    } else {
      t18 = $[31];
    }
    t17 = t18;
  }
  const fundSubmitLabel = t17;
  let t18;
  bb1: {
    if (isSubmitting) {
      let t19;
      if ($[32] !== tWithdraw) {
        t19 = tWithdraw("confirming");
        $[32] = tWithdraw;
        $[33] = t19;
      } else {
        t19 = $[33];
      }
      t18 = t19;
      break bb1;
    }
    if (!canWithdraw) {
      let t19;
      if ($[34] !== tWithdraw) {
        t19 = tWithdraw("confirm");
        $[34] = tWithdraw;
        $[35] = t19;
      } else {
        t19 = $[35];
      }
      t18 = t19;
      break bb1;
    }
    let t19;
    if ($[36] !== tWithdraw) {
      t19 = tWithdraw("confirm");
      $[36] = tWithdraw;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    t18 = t19;
  }
  const withdrawSubmitLabel = t18;
  const handleFund = function handleFund() {
    if (fundSubmitDisabled || !activeWallet || outcomes.length === 0 || abortControllerRef.current !== null) {
      return;
    }
    submitGuardedTrade(async () => {
      if (await dailyBackingLimit.blocks(usdcAmount, proposalPubkey)) {
        return limitBlockedResult();
      }
      const result = await runFund({
        activeWallet,
        proposalPubkey,
        backingAccounts,
        usdcAmount,
        fundStakeOutcomeIndex,
        network,
        formRootRef,
        submitTx,
        signingDeps,
        turnkeyDeps,
        queryClient,
        fundingControllers,
        formatBalance,
        shouldCelebrate: () => !launchingRef.current
      });
      if (result.ok) {
        dailyBackingLimit.record(usdcAmount);
      }
      return result;
    });
  };
  const handleWithdraw = function handleWithdraw() {
    if (withdrawSubmitDisabled || !activeWallet || userStake === null || isTradeSubmissionBlocked(true) || abortControllerRef.current !== null) {
      return;
    }
    runWithdraw({
      activeWallet,
      proposalPubkey,
      backingAccounts,
      network,
      shareBalanceD6Str: userStake.totalAmount,
      netReturnUsdc,
      submitTx,
      signingDeps,
      turnkeyDeps,
      queryClient,
      invalidateWalletBalances,
      abortControllerRef,
      setActiveAbortController,
      formatBalance
    });
  };
  let t19;
  if ($[38] !== onAmountInputChange) {
    t19 = function handleQuickAmount(amount) {
      onAmountInputChange(String(amount));
    };
    $[38] = onAmountInputChange;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  const handleQuickAmount = t19;
  const handleMaxClick = function handleMaxClick() {
    const maxNetStake = balance * (10000 - feeBpsBacking) / 10000;
    onAmountInputChange(String(Math.floor(maxNetStake * 100) / 100));
  };
  const isFund = direction === "fund";
  const hasStakeSummary = totalStakedUsdc > 0;
  const t20 = "flex flex-col gap-3";
  let t21;
  if ($[40] !== direction || $[41] !== directionSwitchDisabled || $[42] !== showTabs) {
    t21 = showTabs && <ProposalFormDirectionTabs direction={direction} disabled={directionSwitchDisabled} onDirectionChange={setDirection} />;
    $[40] = direction;
    $[41] = directionSwitchDisabled;
    $[42] = showTabs;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== hasUnknownTrade || $[45] !== tTrade) {
    t22 = hasUnknownTrade ? <output className="text-caption text-foreground-secondary">{tTrade("submission_status_unknown")}</output> : null;
    $[44] = hasUnknownTrade;
    $[45] = tTrade;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== tWithdraw || $[48] !== withdrawOnly) {
    t23 = withdrawOnly && <span className="text-heading-xs">{tWithdraw("title")}</span>;
    $[47] = tWithdraw;
    $[48] = withdrawOnly;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== formatBalance || $[51] !== hasStakeSummary || $[52] !== totalStakedUsdc) {
    t24 = hasStakeSummary && <StakeSummaryRow formattedTotal={formatBalance(totalStakedUsdc)} />;
    $[50] = formatBalance;
    $[51] = hasStakeSummary;
    $[52] = totalStakedUsdc;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  const t25 = isFund && !withdrawOnly ? <FundTab amountInput={amountInput} onAmountInputChange={onAmountInputChange} currencySymbol={currencySymbol} formattedBalance={formatBalance(balance)} formattedQuickAmounts={quickAmounts} onQuickAmount={handleQuickAmount} onMaxClick={handleMaxClick} onSubmit={handleFund} isSubmitting={anySubmissionPending} inputDisabled={fundingBlocked} exceedsBalance={exceedsBalance} isValidFundAmount={isValidFundAmount} fundSubmitDisabled={fundSubmitDisabled} formattedNetStake={formatBalance(usdcAmount)} formattedBackingFee={formatBalance(backingFeeUsdc)} formattedTotalCost={formatBalance(totalCostUsdc)} /> : <WithdrawTab canWithdraw={canWithdraw} withdrawSubmitDisabled={withdrawSubmitDisabled} withdrawSubmitLoading={isSubmitting} withdrawSubmitLabel={withdrawSubmitLabel} formattedUnbackingFee={formatBalance(unbackingFeeUsdc)} formattedNetReturn={formatBalance(netReturnUsdc)} onSubmit={handleWithdraw} />;
  let t26;
  if ($[54] !== fundSubmitDisabled || $[55] !== fundSubmitLabel || $[56] !== fundingBlocked || $[57] !== handleFund || $[58] !== isFund || $[59] !== launching || $[60] !== withdrawOnly) {
    t26 = isFund && !withdrawOnly ? <FundSubmitButton disabled={fundSubmitDisabled} loading={fundingBlocked || launching} onSubmit={handleFund} label={fundSubmitLabel} /> : null;
    $[54] = fundSubmitDisabled;
    $[55] = fundSubmitLabel;
    $[56] = fundingBlocked;
    $[57] = handleFund;
    $[58] = isFund;
    $[59] = launching;
    $[60] = withdrawOnly;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== formRootRef || $[63] !== rewardsPanel || $[64] !== t21 || $[65] !== t22 || $[66] !== t23 || $[67] !== t24 || $[68] !== t25 || $[69] !== t26) {
    t27 = <div ref={formRootRef} className={t20}>{t21}{t22}{t23}{t24}{t25}{rewardsPanel}{t26}</div>;
    $[62] = formRootRef;
    $[63] = rewardsPanel;
    $[64] = t21;
    $[65] = t22;
    $[66] = t23;
    $[67] = t24;
    $[68] = t25;
    $[69] = t26;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  const formContent = t27;
  if (bare) {
    return formContent;
  }
  let t28;
  if ($[71] !== formContent) {
    t28 = <Panel className="overflow-hidden"><PanelContent className="px-4">{formContent}</PanelContent></Panel>;
    $[71] = formContent;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  return t28;
}
function _temp5(s_1) {
  return s_1.swapPending;
}
function _temp4(s_0) {
  return s_0.outstanding.some(_temp3);
}
function _temp3(entry) {
  return entry.status === "unknown";
}
function _temp2(s) {
  return s.outstanding.length;
}
function _temp() {
  return new Map();
}